import { ActionReducer, Action } from '@ngrx/store';
import { startLoading, stopLoading } from './global.actions';

/**
 * Loading Meta-Reducer
 * 
 * Automatically tracks loading state for all NgRx actions following naming conventions:
 * - Actions without 'Success' or 'Error' suffix → Start loading
 * - Actions with 'Success' or 'Error' suffix → Stop loading
 * 
 * Example:
 * - findRoads → startLoading('findRoads')
 * - findRoadsSuccess → stopLoading('findRoads')
 * - findRoadsError → stopLoading('findRoads')
 * 
 * This eliminates the need to manually dispatch loading actions in effects.
 */

export function loadingMetaReducer(reducer: ActionReducer<any>): ActionReducer<any> {
    return (state, action) => {
        // First, run the original reducer
        let nextState = reducer(state, action);

        // Skip global actions to prevent infinite loops
        if (action.type.startsWith('[Global]')) {
            return nextState;
        }

        // Extract the base action type (remove Success/Error suffix)
        const baseActionType = getBaseActionType(action.type);

        // Determine if this is a start, success, or error action
        if (isSuccessAction(action.type) || isErrorAction(action.type)) {
            // Stop loading for the base action
            nextState = reducer(nextState, stopLoading({ actionType: baseActionType }));
        } else if (isLoadableAction(action.type)) {
            // Start loading for this action
            nextState = reducer(nextState, startLoading({ actionType: action.type }));
        }

        return nextState;
    };
}

/**
 * Check if an action should trigger loading state
 * Excludes certain action patterns that shouldn't show loading
 */
function isLoadableAction(actionType: string): boolean {
    // Skip actions that shouldn't trigger loading
    const skipPatterns = [
        '@ngrx',           // NgRx internal actions
        '[Router',         // Router actions
        '[Global]',        // Global state actions
        'Success',         // Success actions
        'Error',           // Error actions
        'Failure',         // Failure actions
    ];

    return !skipPatterns.some(pattern => actionType.includes(pattern));
}

/**
 * Check if action is a success action
 */
function isSuccessAction(actionType: string): boolean {
    return actionType.includes('Success');
}

/**
 * Check if action is an error action
 */
function isErrorAction(actionType: string): boolean {
    return actionType.includes('Error') || actionType.includes('Failure');
}

/**
 * Extract base action type by removing Success/Error/Failure suffix
 * 
 * Examples:
 * - '[Common] Find Roads Success' → '[Common] Find Roads'
 * - '[Common] Find Roads Error' → '[Common] Find Roads'
 * - '[Common] Find Roads' → '[Common] Find Roads'
 */
function getBaseActionType(actionType: string): string {
    return actionType
        .replace(/\s+Success$/, '')
        .replace(/\s+Error$/, '')
        .replace(/\s+Failure$/, '');
}
